import { useEffect, useMemo, useState } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import { Calendar, ChevronDown, ChevronRight, GraduationCap, Search, Users } from 'lucide-react';
import LoadingSpinner from '../../common/LoadingSpinner';
import EnrollmentPeriodBar from './EnrollmentPeriodBar';
import WeekdayPatternPicker from './WeekdayPatternPicker';
import { useEnrollmentPeriod } from './useEnrollmentPeriod';
import {
    formatClock,
    groupSessionsByMonth,
    idKey,
    isCancelledSession,
    isSessionFull,
    listUpcomingMonths,
    sessionInRange,
    todayRomeDateString,
} from '../../../utils/courseSessionPattern';

function monthLabel(monthKey, language) {
    const [year, month] = monthKey.split('-').map(Number);
    return new Date(year, month - 1, 1).toLocaleDateString(language || 'it-IT', {
        month: 'long',
        year: 'numeric',
    });
}

function sessionDayLabel(sessionDate, language) {
    const [year, month, day] = String(sessionDate).slice(0, 10).split('-').map(Number);
    return new Date(year, month - 1, day).toLocaleDateString(language || 'it-IT', {
        weekday: 'short',
        day: 'numeric',
        month: 'short',
    });
}

export default function CourseSessionSelector({ selectedSessions, onSelectionChange }) {
    const { getAuthenticatedApi } = useAuth();
    const { t, i18n } = useTranslation();
    const period = useEnrollmentPeriod();
    const [courses, setCourses] = useState([]);
    const [sessions, setSessions] = useState({});
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [openDates, setOpenDates] = useState({});

    useEffect(() => {
        let cancelled = false;

        const fetchCourses = async () => {
            try {
                setLoading(true);
                const api = getAuthenticatedApi();
                const coursesResponse = await api.get('/gym_courses.php?action=list_courses');
                const coursesData = (coursesResponse.data.courses || []).filter(
                    (course) => course.status === 'active'
                );

                const months = listUpcomingMonths(6);
                const sessionsResponses = await Promise.all(
                    months.map((month) =>
                        api.get(`/gym_courses.php?action=list_sessions&month=${month}`)
                    )
                );
                const today = todayRomeDateString();
                const sessionsData = {};
                sessionsResponses
                    .flatMap((response) => response.data.sessions || [])
                    .forEach((session) => {
                        if (String(session.session_date).slice(0, 10) < today) return;
                        if (isCancelledSession(session)) return;
                        if (!sessionsData[session.course_id]) sessionsData[session.course_id] = [];
                        sessionsData[session.course_id].push(session);
                    });

                Object.keys(sessionsData).forEach((courseId) => {
                    sessionsData[courseId].sort((a, b) =>
                        String(a.session_date).localeCompare(String(b.session_date))
                    );
                });

                if (!cancelled) {
                    setCourses(coursesData);
                    setSessions(sessionsData);
                }
            } catch (err) {
                console.error('Errore caricamento corsi:', err);
            } finally {
                if (!cancelled) setLoading(false);
            }
        };

        fetchCourses();
        return () => {
            cancelled = true;
        };
        // Il client autenticato non deve rilanciare il caricamento a ogni render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const filteredCourses = useMemo(() => {
        return courses.filter((course) => {
            const haystack = `${course.title} ${course.description || ''}`.toLowerCase();
            const matchesSearch = haystack.includes(searchTerm.trim().toLowerCase());
            const matchesCategory = categoryFilter === 'all' || course.category === categoryFilter;
            return matchesSearch && matchesCategory;
        });
    }, [courses, searchTerm, categoryFilter]);

    const categories = ['all', ...new Set(courses.map((course) => course.category).filter(Boolean))];

    const selectedKeys = useMemo(
        () => new Set(selectedSessions.map(idKey)),
        [selectedSessions]
    );

    const replaceCourseSelection = (courseId, nextSelected) => {
        const courseIds = new Set((sessions[courseId] || []).map((session) => idKey(session.id)));
        const rest = selectedSessions.filter((id) => !courseIds.has(idKey(id)));
        const kept = nextSelected.filter((id) => courseIds.has(idKey(id)));
        onSelectionChange([...rest, ...kept]);
    };

    if (loading) {
        return (
            <div className="flex justify-center py-16">
                <LoadingSpinner />
            </div>
        );
    }

    return (
        <div className="flex h-full min-h-0 flex-1 flex-col">
            <div className="shrink-0 space-y-4 border-b border-gray-200 px-4 py-4 dark:border-gray-800 sm:px-6">
                <div className="flex items-start gap-3">
                    <span className="mt-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-indigo-600 text-white">
                        <GraduationCap size={20} aria-hidden />
                    </span>
                    <div>
                        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-50">
                            {t('gym.bulkEnrollment.courses.title')}
                        </h2>
                        <p className="mt-0.5 text-sm text-gray-500 dark:text-gray-400">
                            {t('gym.bulkEnrollment.courses.subtitle')}
                        </p>
                    </div>
                </div>

                <EnrollmentPeriodBar
                    preset={period.preset}
                    from={period.from}
                    to={period.to}
                    today={period.today}
                    onPreset={period.applyPreset}
                    onFromChange={period.updateFrom}
                    onToChange={period.updateTo}
                />

                <div className="grid grid-cols-1 gap-2 sm:grid-cols-[minmax(0,1fr)_220px]">
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
                        <input
                            type="text"
                            placeholder={t('gym.bulkEnrollment.courses.searchPlaceholder')}
                            value={searchTerm}
                            onChange={(event) => setSearchTerm(event.target.value)}
                            className="w-full rounded-lg border border-gray-300 bg-white py-2 pl-10 pr-4 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                        />
                    </div>
                    <select
                        value={categoryFilter}
                        onChange={(event) => setCategoryFilter(event.target.value)}
                        className="rounded-lg border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 focus:border-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500/30 dark:border-gray-700 dark:bg-gray-900 dark:text-gray-100"
                    >
                        {categories.map((category) => (
                            <option key={category} value={category}>
                                {category === 'all'
                                    ? t('gym.bulkEnrollment.courses.allCategories')
                                    : category}
                            </option>
                        ))}
                    </select>
                </div>
            </div>

            <div className="min-h-0 flex-1 space-y-3 overflow-y-auto px-4 py-4 sm:px-6">
                {filteredCourses.length === 0 ? (
                    <div className="py-12 text-center">
                        <GraduationCap size={40} className="mx-auto mb-2 text-gray-400" />
                        <p className="text-sm text-gray-500 dark:text-gray-400">
                            {t('gym.bulkEnrollment.courses.noCoursesFound')}
                        </p>
                    </div>
                ) : (
                    filteredCourses.map((course) => {
                        const courseSessions = sessions[course.id] || [];
                        const selectedCount = courseSessions.filter((session) =>
                            selectedKeys.has(idKey(session.id))
                        ).length;
                        const datesOpen = Boolean(openDates[course.id]);
                        const visibleSessions = courseSessions.filter((session) =>
                            sessionInRange(session, period.from, period.to)
                        );
                        const groups = groupSessionsByMonth(visibleSessions);

                        return (
                            <article
                                key={course.id}
                                className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-800 dark:bg-gray-900"
                            >
                                <div className="mb-3 flex items-start gap-3">
                                    {course.logo_filename ? (
                                        <img
                                            src={`/api/serve_image.php?filename=${course.logo_filename}&type=logo`}
                                            alt=""
                                            className="h-11 w-11 shrink-0 rounded-lg border border-gray-200 object-cover dark:border-gray-700"
                                        />
                                    ) : (
                                        <span
                                            className="mt-1 h-3.5 w-3.5 shrink-0 rounded-full"
                                            style={{ backgroundColor: course.color || '#4F46E5' }}
                                            aria-hidden
                                        />
                                    )}
                                    <div className="min-w-0 flex-1">
                                        <h3 className="truncate text-base font-semibold text-gray-900 dark:text-gray-50">
                                            {course.title}
                                        </h3>
                                        <p className="text-xs text-gray-500 dark:text-gray-400">
                                            {course.category ? `${course.category} · ` : ''}
                                            {t('gym.bulkEnrollment.courses.futureSessionsCount', {
                                                count: courseSessions.length,
                                            })}
                                            {selectedCount > 0
                                                ? ` · ${t('gym.bulkEnrollment.courses.selectedOnCourse', { count: selectedCount })}`
                                                : ''}
                                        </p>
                                    </div>
                                </div>

                                {courseSessions.length === 0 ? (
                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                        {t('gym.bulkEnrollment.courses.noFutureSessions')}
                                    </p>
                                ) : (
                                    <>
                                        <WeekdayPatternPicker
                                            sessions={courseSessions}
                                            selectedIds={selectedSessions}
                                            onChange={(next) => replaceCourseSelection(course.id, next)}
                                            rangeFrom={period.from}
                                            rangeTo={period.to}
                                            isBlocked={isSessionFull}
                                        />

                                        <button
                                            type="button"
                                            onClick={() =>
                                                setOpenDates((prev) => ({
                                                    ...prev,
                                                    [course.id]: !prev[course.id],
                                                }))
                                            }
                                            className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-indigo-700 hover:underline dark:text-indigo-300"
                                        >
                                            {datesOpen ? (
                                                <ChevronDown size={16} aria-hidden />
                                            ) : (
                                                <ChevronRight size={16} aria-hidden />
                                            )}
                                            {datesOpen
                                                ? t('gym.bulkEnrollment.courses.hideDates')
                                                : t('gym.bulkEnrollment.courses.customizeDates')}
                                        </button>

                                        {datesOpen && (
                                            <div className="mt-3 space-y-3">
                                                {groups.length === 0 ? (
                                                    <p className="text-sm text-gray-500 dark:text-gray-400">
                                                        {t('gym.bulkEnrollment.courses.noLessonsInPeriod')}
                                                    </p>
                                                ) : (
                                                    groups.map((group) => (
                                                        <div key={group.key}>
                                                            <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
                                                                {monthLabel(group.key, i18n.language)}
                                                            </p>
                                                            <ul className="space-y-1">
                                                                {group.sessions.map((session) => {
                                                                    const full = isSessionFull(session);
                                                                    const checked = selectedKeys.has(idKey(session.id));
                                                                    return (
                                                                        <li key={session.id}>
                                                                            <label
                                                                                className={`flex items-center gap-3 rounded-lg px-2 py-2 ${
                                                                                    full
                                                                                        ? 'opacity-60'
                                                                                        : 'cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-800/70'
                                                                                } ${checked ? 'bg-indigo-50 dark:bg-indigo-950/30' : ''}`}
                                                                            >
                                                                                <input
                                                                                    type="checkbox"
                                                                                    disabled={full}
                                                                                    checked={checked}
                                                                                    onChange={() => {
                                                                                        const next = checked
                                                                                            ? selectedSessions.filter(
                                                                                                  (id) => idKey(id) !== idKey(session.id)
                                                                                              )
                                                                                            : [...selectedSessions, session.id];
                                                                                        onSelectionChange(next);
                                                                                    }}
                                                                                    className="h-4 w-4 rounded text-indigo-600 focus:ring-indigo-500"
                                                                                />
                                                                                <Calendar size={14} className="text-gray-400" aria-hidden />
                                                                                <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                                                                    {sessionDayLabel(session.session_date, i18n.language)}
                                                                                </span>
                                                                                <span className="text-sm text-gray-600 dark:text-gray-300">
                                                                                    {formatClock(session.start_time)}–{formatClock(session.end_time)}
                                                                                </span>
                                                                                <span className="ml-auto inline-flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
                                                                                    <Users size={12} aria-hidden />
                                                                                    {session.current_participants || 0}/{session.max_participants || '∞'}
                                                                                    {full
                                                                                        ? ` · ${t('gym.bulkEnrollment.courses.sessionFull')}`
                                                                                        : ''}
                                                                                </span>
                                                                            </label>
                                                                        </li>
                                                                    );
                                                                })}
                                                            </ul>
                                                        </div>
                                                    ))
                                                )}
                                            </div>
                                        )}
                                    </>
                                )}
                            </article>
                        );
                    })
                )}
            </div>
        </div>
    );
}
